window.__cartQueue = (() => {
  let chain = Promise.resolve();
  return {
    enqueue(asyncFn) {
      const next = chain.then(asyncFn, asyncFn);
      chain = next.catch((err) => {
        console.error("Cart queue operation failed:", err);
      });
      return next;
    },
  };
})();

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

function removeSubMenu() {
  if (document.querySelectorAll(".submenu-open").length == 1) {
    document.body.classList.remove("submenu-open");
    document.querySelector(".menu-drawer")?.classList.remove("full-height");
  }
}

class SectionId {
  static separator() {
    return "__";
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(this.separator())[0];
  }
  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(this.separator())[1];
  }
  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${this.separator()}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode?.querySelectorAll("[id], [form]")?.forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute("form", `${element.form.getAttribute("id")}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open"));

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    if (event.currentTarget.classList.contains("header__icon--summary")) {
      event.currentTarget.closest(".header-wrapper").classList.toggle("header-opened");
    }
    if (event.target.closest(".header__icon--menu")) {
      document.querySelector(".utility-bar")?.classList.add("hide");
    }
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const primaryButtons = document.querySelectorAll(".button--primary");
primaryButtons.forEach((button) => {
  button.addEventListener("click", function () {
    this.focus();
  });
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  if (elementToFocus?.id === "Search-In-Modal") return;
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  if (!elementToFocus) return;
  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + "pauseVideo" + '","args":""}', "*");
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
  removeSubMenu();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === "minus") {
      this.input.value = parseInt(this.input.min);
    }
  }

 validateQtyRules() {
    let value = parseInt(this.input.value);

    const min = this.input.min ? parseInt(this.input.min) : 1;
    const max = this.input.max ? parseInt(this.input.max) : null;
    const isOffline = this.input.dataset.isOffline === 'true';
 
    if (isNaN(value) || value < min) {
      value = min;
      this.input.value = min;
    }

    const buttonMinus = this.querySelector(".quantity__button[name='minus']");
    if (buttonMinus) {
      buttonMinus.classList.toggle("disabled", value <= min || max === 0 || isOffline);
      buttonMinus.disabled = isOffline;
    }

    if (max !== null) {
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) {
        buttonPlus.classList.toggle("disabled", value >= max || value === 0 || isOffline);
        buttonPlus.disabled = isOffline;
      }
 
      if (value > max) {
        this.input.value = max;
      }
    } else if (isOffline) {
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) {
        buttonPlus.classList.toggle("disabled", true);
        buttonPlus.disabled = true;
      }
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options["hideElement"] || province_domid);

  Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)",
    ).forEach((button) => button.addEventListener("click", this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary"))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
      summaryElement.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        document.body.classList.add("submenu-open");
        document.querySelector(".menu-drawer")?.classList.add("full-height");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener("transitionend", addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
      submenu.classList.remove("submenu-open");
    });
    removeSubMenu();
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
    removeSubMenu();
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(detailsElement.closest("details[open]"), detailsElement.querySelector("summary"));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.scrollValue = 0;
    this.onBackdropClick = this.onBackdropClick.bind(this);
  }

  lockScroll() {
    this.scrollValue = window.scrollY;
    document.body.dataset.scrollLock = this.scrollValue;
    document.body.classList.add("scroll-locked");
    document.body.style.top = `-${this.scrollValue}px`;
  }

  unlockScroll() {
    const scrollValue = parseInt(document.body.dataset.scrollLock || 0, 10);
    document.body.classList.remove("scroll-locked");
    document.body.style.top = "";
    window.scrollTo(0, scrollValue);
    delete document.body.dataset.scrollLock;
  }

  performMenuDrawerCleanup() {
    this.unlockScroll();

    document.querySelectorAll(".menu-drawer details[open]").forEach((detail) => {
      detail.removeAttribute("open");
    });

    document.querySelectorAll("details.menu-opening[open], details-disclosure[open]").forEach((detail) => {
      detail.removeAttribute("open");
    });

    document.querySelectorAll(".menu-drawer__menu.has-submenu.submenu-open").forEach((nav) => {
      nav.classList.remove("submenu-open");
    });

    document.body.classList.remove("submenu-open");
    document.querySelector(".menu-drawer")?.classList.remove("full-height");
    document.querySelector("sticky-header")?.classList.remove("header-opened");
    document.querySelector(".utility-bar")?.classList.remove("hide");
    document.body.classList.remove("overflow-hidden-tablet");
  }

  onBackdropClick(event) {
    if (!this.mainDetailsToggle || !this.mainDetailsToggle.hasAttribute("open")) return;

    const clickedElement = event.target;
    const menuDrawer = clickedElement.closest(".menu-drawer");
    const summaryButton = clickedElement.closest("summary.header__icon--menu");
    const isClickOnContainer = event.currentTarget === event.target || event.target === this.mainDetailsToggle;

    if (
      !menuDrawer &&
      !summaryButton &&
      (isClickOnContainer || clickedElement.closest(".menu-drawer-container") === this.mainDetailsToggle)
    ) {
      event.stopPropagation();
      this.performMenuDrawerCleanup();
      if (this.mainDetailsToggle) {
        this.mainDetailsToggle.removeEventListener("click", this.onBackdropClick, true);
      }
      this.header.classList.remove("menu-open");
      window.removeEventListener("resize", this.onResize);
      super.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary"));
    }
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset || this.closest(".header-wrapper").classList.contains("header-wrapper--border-bottom") ? 1 : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
      if (this.mainDetailsToggle) {
        this.mainDetailsToggle.addEventListener("click", this.onBackdropClick, true);
      }
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    this.lockScroll();
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    if (this.mainDetailsToggle) {
      this.mainDetailsToggle.addEventListener("click", this.onBackdropClick, true);
    }
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
    this.performMenuDrawerCleanup();
    super.closeMenuDrawer(event, elementToFocus);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener("click", this.hide.bind(this, false));
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (event.pointerType === "mouse" && !event.target.closest("deferred-media, product-model")) this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section")?.id.replace("shopify-section-", "");
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, "text/html");
            const sourceQty = html.querySelector(".quick-order-list-container").parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`),
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(true));

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(content.querySelector("video, model-viewer, iframe"));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == "VIDEO" && deferredElement.getAttribute("autoplay")) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
    this.addDragToScroll();
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) {
      this.disableSliderButtons();
      return;
    }
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    if (this.totalPages <= 1) {
      this.disableSliderButtons();
      return;
    }
    this.toggleDragScroll(true);
    this.update();
  }

  disableSliderButtons() {
    if (this.prevButton) this.prevButton.setAttribute("disabled", "disabled");
    if (this.nextButton) this.nextButton.setAttribute("disabled", "disabled");
    this.toggleDragScroll(false);
  }

  toggleDragScroll(enabled) {
    if (!this.slider) return;
    this.dragScrollEnabled = enabled;
    this.slider.style.cursor = enabled ? '' : 'default';
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (!this.enableSliderLooping) {
      const EDGE_THRESHOLD = 30;
      const maxScroll = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);

      const atStart = this.slider.scrollLeft <= EDGE_THRESHOLD;
      const atEnd = maxScroll - this.slider.scrollLeft <= EDGE_THRESHOLD;

      this.prevButton.toggleAttribute("disabled", atStart);
      this.nextButton.toggleAttribute("disabled", atEnd);
    }
  }

  isSlideVisible(element, offset = 0) {
    const scrollLeft = this.slider?.scrollLeft;
    const visibleRightEdge = this.slider?.clientWidth + scrollLeft - offset;
    const elementLeft = element?.offsetLeft;
    const elementRight = elementLeft + element?.clientWidth;

    return elementRight <= visibleRightEdge + 1 && elementLeft >= scrollLeft - 1;
  }

  onButtonClick(event) {
    event.preventDefault();
    this.sliderItemOffset = this.slider.querySelector(".slider__slide").offsetWidth;
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollLeft = position;
    requestAnimationFrame(() => this.update());
  }

  addDragToScroll() {
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let isDragging = false;

    const anchorTags = this.slider.querySelectorAll("a");
    anchorTags.forEach((anchor) => {
      anchor.setAttribute("draggable", "false");
    });

    this.slider.addEventListener("mousedown", (e) => {
      if (this.dragScrollEnabled === false) return;
      isDown = true;
      isDragging = false;
      startX = e.pageX - this.slider.offsetLeft;
      scrollLeft = this.slider.scrollLeft;
      this.slider.classList.add("active");
    });

    this.slider.addEventListener("mouseleave", () => {
      if (isDown) {
        isDown = false;
        isDragging = false;
        this.slider.classList.remove("active");
      }
    });

    document.addEventListener("mouseup", (e) => {
      if (isDown) {
        isDown = false;
        this.slider.classList.remove("active");

        if (!isDragging && e.button === 0) {
          const anchor = e.target.closest(".slider__slide");
          const anchorHref = anchor?.dataset.anchor;
          if (anchorHref) {
            e.preventDefault();
            window.location.href = anchorHref;
          }
        }
      }
      isDragging = false;
    });

    this.slider.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      e.preventDefault();

      isDragging = true;
      const x = e.pageX - this.slider.offsetLeft;
      const walk = (x - startX) * 2;
      this.slider.scrollLeft = scrollLeft - walk;
    });

    this.slider.addEventListener("click", (e) => {
      if (isDragging) {
        e.preventDefault();
      }
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    // Check if this is the hero slideshow
    this.isHeroSlideshow = this.hasAttribute("data-hero-slideshow");

    if (this.isHeroSlideshow) {
      this.initInfiniteLoop();
    }

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    
    // Only add infinite loop handler for hero slideshow
    if (this.isHeroSlideshow) {
      this.slider.addEventListener("scroll", this.handleInfiniteLoop.bind(this));
    }
    
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  initInfiniteLoop() {
    // Only clone if we have more than 1 slide
    const slides = this.slider.querySelectorAll(".slideshow__slide");
    if (slides.length > 1) {
      this.realSlideCount = slides.length;
      
      // Store reference to real first slide before cloning
      const realFirstSlide = slides[0];
      
      // Clone last slide and prepend it (for backward loop)
      const lastSlide = slides[slides.length - 1];
      const clonedLastSlide = lastSlide.cloneNode(true);
      clonedLastSlide.classList.add("slideshow__slide--cloned", "slideshow__slide--cloned-last");
      clonedLastSlide.setAttribute("aria-hidden", "true");
      clonedLastSlide.setAttribute("data-cloned", "true");
      this.slider.insertBefore(clonedLastSlide, slides[0]);
      this.clonedLastSlide = clonedLastSlide;
      
      // Clone first slide and append it (for forward loop)
      const clonedFirstSlide = realFirstSlide.cloneNode(true);
      clonedFirstSlide.classList.add("slideshow__slide--cloned", "slideshow__slide--cloned-first");
      clonedFirstSlide.setAttribute("aria-hidden", "true");
      clonedFirstSlide.setAttribute("data-cloned", "true");
      this.slider.appendChild(clonedFirstSlide);
      this.clonedFirstSlide = clonedFirstSlide;
      
      // Update sliderFirstItemNode to point to real first slide 
      this.sliderFirstItemNode = realFirstSlide;
      
      // Set initial scroll position to the real first slide
      const slideWidth = realFirstSlide.clientWidth;
      this.slider.scrollLeft = slideWidth;
      this.initialScrollSet = true;
    }
  }

  handleInfiniteLoop() {
    // Check if we've scrolled to the cloned slides
    if (!this.clonedFirstSlide || !this.clonedLastSlide) return;
    
    const slideWidth = this.sliderFirstItemNode.clientWidth;
    const scrollLeft = this.slider.scrollLeft;
    
    const clonedFirstPosition = slideWidth * (this.realSlideCount + 1);
    const clonedLastPosition = 0;
    const realFirstPosition = slideWidth;
    const realLastPosition = slideWidth * this.realSlideCount;
    
    // If we've scrolled forward to the cloned first slide, reset to real first slide
    if (scrollLeft >= clonedFirstPosition - 1) {
      this.slider.style.scrollBehavior = "auto";
      this.slider.scrollLeft = realFirstPosition;
      this.currentPage = 1;
      setTimeout(() => {
        this.slider.style.scrollBehavior = "smooth";
      }, 50);
    }
    // If we've scrolled backward to the cloned last slide, reset to real last slide
    else if (scrollLeft <= clonedLastPosition + 1) {
      this.slider.style.scrollBehavior = "auto";
      this.slider.scrollLeft = realLastPosition;
      this.currentPage = this.realSlideCount;
      setTimeout(() => {
        this.slider.style.scrollBehavior = "smooth";
      }, 50);
    }
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener("click", this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      if (this.isHeroSlideshow) {
        // Hero slideshow: continue backward to cloned last slide for seamless loop
        this.slideScrollPosition = this.slider.scrollLeft - this.sliderFirstItemNode.clientWidth;
      } else {
        // Other slideshows: jump to end
        this.slideScrollPosition =
          this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
      }
    } else if (isLastSlide && event.currentTarget.name === "next") {
      if (this.isHeroSlideshow) {
        // Hero slideshow: continue forward to cloned first slide for seamless loop
        this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth;
      } else {
        // Other slideshows: jump back to start
        this.slideScrollPosition = 0;
      }
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    
    let activeDotIndex;
    if (this.isHeroSlideshow) {
      // Hero slideshow: handle dot navigation for infinite loop (wrap to first dot if beyond real slides)
      activeDotIndex = this.currentPage > this.sliderControlButtons.length ? 0 : this.currentPage - 1;
    } else {
      // Other slideshows: use normal dot navigation
      activeDotIndex = this.currentPage - 1;
    }
    
    if (this.sliderControlButtons[activeDotIndex]) {
      this.sliderControlButtons[activeDotIndex].classList.add("slider-counter__link--active");
      this.sliderControlButtons[activeDotIndex].setAttribute("aria-current", true);
    }
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    let slideScrollPosition;
    
    if (this.isHeroSlideshow) {
      // Hero slideshow: always continue forward for infinite loop effect
      slideScrollPosition = this.slider.scrollLeft + this.sliderItemOffset;
    } else {
      // Other slideshows: use original behavior (jump back to start)
      slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;
    }

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      // Skip cloned slides for visibility logic
      if (this.isHeroSlideshow && item.hasAttribute("data-cloned")) return;
      
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === "next" && !isLastSlide) || (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide?.classList.add(`${animationClassOut}-${direction}`);
    nextSlide?.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide?.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide?.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const targetIndex = this.sliderControlLinksArray.indexOf(event.currentTarget);
    let slideScrollPosition;
    
    if (this.isHeroSlideshow) {
      // For hero slideshow, account for cloned last slide at the beginning
      slideScrollPosition = this.sliderFirstItemNode.clientWidth * (targetIndex + 1);
    } else {
      // Other slideshows: use original relative calculation
      slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth *
          (targetIndex + 1 - this.currentPage);
    }
    
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initCustomSelect(this);

    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  initCustomSelect(element) {
    element.querySelectorAll(".custom-select").forEach((customSelect) => {
      const customOptions = customSelect.nextElementSibling;
      const hiddenSelect = customSelect.closest(".custom-select-wrapper").nextElementSibling;
      customSelect.addEventListener("click", () => {
        const willOpen = customOptions.style.display !== "block";
        customOptions.style.display = willOpen ? "block" : "none";
        customSelect.classList.toggle("open", willOpen);
        if (willOpen) {
          customOptions.classList.toggle("has-scrollbar", customOptions.scrollHeight > customOptions.clientHeight);
        }
      });

      function updatePreviewSwatch({ color, image }) {
        const preview =
          customSelect.querySelector(".product-square-swatch") || customSelect.querySelector(".color-swatch");
        if (!preview) return;

        if (image) {
          let img = preview.querySelector("img");
          if (!img) {
            img = document.createElement("img");
            img.setAttribute("width", "100%");
            img.setAttribute("height", "100%");
            img.setAttribute("alt", "");
            preview.appendChild(img);
          }
          preview.style.background = "transparent";
          img.src = image;
          preview.dataset.swatchType = "image";
          preview.dataset.swatchImage = image;
          delete preview.dataset.swatchColor;
        } else {
          const existingImg = preview.querySelector("img");
          if (existingImg) existingImg.remove();
          preview.style.backgroundColor = color || "transparent";
          preview.dataset.swatchType = "color";
          preview.dataset.swatchColor = color || "";
          delete preview.dataset.swatchImage;
        }
      }

      function readSwatchData(option) {
        const optSwatch = option.querySelector(".color-swatch");
        const inlineBackground =
          (optSwatch?.style?.getPropertyValue("--swatch--background") || optSwatch?.style?.backgroundColor || "").trim();
        const swatchImage =
          optSwatch?.dataset?.swatchImage ||
          optSwatch?.querySelector("img")?.getAttribute("src") ||
          option.dataset.optionSwatchImage ||
          "";
        const swatchColor =
          optSwatch?.dataset?.swatchColor || option.dataset.optionSwatchValue || inlineBackground || "";
        const swatchType =
          optSwatch?.dataset?.swatchType || (swatchImage ? "image" : swatchColor ? "color" : undefined);

        return { swatchType, swatchImage, swatchColor };
      }

      customOptions.querySelectorAll(".custom-option").forEach((option) => {
        option.addEventListener("click", () => {
          const selectedValue = option.dataset.value;
          const selectedUndertonesValue = option.dataset.ut;
          const labelText = option.dataset.value || option.textContent.trim();
          customSelect.querySelector(".option-value").textContent = labelText;

          const selectedNewBadge = customSelect.querySelector(".dropdown-new-badge");
          if (selectedNewBadge) {
            selectedNewBadge.style.display = option.dataset.isNew === "true" ? "" : "none";
          }

          const variantUndertoneEl = customSelect.querySelector(".variant-undertone");
          if (selectedUndertonesValue && variantUndertoneEl) {
            const v = selectedUndertonesValue[0].toUpperCase() + selectedUndertonesValue.slice(1);
            variantUndertoneEl.textContent = window.variantStrings.undertoneLabel.replace("[value]", v);
          } else if (variantUndertoneEl) {
            variantUndertoneEl.textContent = "";
          }

          const { swatchType, swatchImage, swatchColor } = readSwatchData(option);

          if ((swatchType === "image" || swatchImage) && swatchImage) {
            updatePreviewSwatch({ image: swatchImage });
          } else if (swatchColor) {
            updatePreviewSwatch({ color: swatchColor });
          } else {
            updatePreviewSwatch({ color: "" });
          }

          hiddenSelect.value = selectedValue;
          hiddenSelect.dispatchEvent(new Event("change", { bubbles: true }));

          customOptions.style.display = "none";
          customSelect.classList.remove("open");
          customOptions.querySelectorAll(".custom-option").forEach((o) => o.classList.remove("selected"));
          option.classList.add("selected");
        });
      });

      document.addEventListener("click", (event) => {
        if (!customSelect.contains(event.target) && !customOptions.contains(event.target)) {
          customOptions.style.display = "none";
          customSelect.classList.remove("open");
        }
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", swatchValue);
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", "unset");
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset",
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }
}

customElements.define("variant-selects", VariantSelects);

// Cache for product recommendations - stores only 1 recommendation at a time to minimize memory usage
if (!window.__productRecommendationsCache) {
  window.__productRecommendationsCache = {
    content: null,
    productId: null,
    sectionId: null,
    contentHeight: 0,
    isLoading: false,
  };
}

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this._isLoading = false;
  }

  /**
   * Check if cache is valid for the given product and section
   */
  isCacheValid(productId, sectionId) {
    const cache = window.__productRecommendationsCache;
    return cache.content && cache.productId === productId && cache.sectionId === sectionId;
  }

  /**
   * Check if element has server-rendered recommendation content
   */
  hasServerRenderedContent() {
    return (
      this.querySelector(".suggested-items__wrapper") ||
      this.querySelector(".grid__item") ||
      this.querySelector("slideshow-component")
    );
  }

  /**
   * Safely update content only if it's different to avoid visual jumps
   */
  updateContentIfDifferent(newContent) {
    const currentContent = this.innerHTML.trim();
    const normalizedNewContent = (newContent || "").trim();

    // Only update if content is actually different
    if (currentContent !== normalizedNewContent) {
      this.innerHTML = normalizedNewContent;
      return true;
    }
    return false;
  }

  /**
   * Restore cached recommendations while minimizing layout shift
   */
  restoreFromCache() {
    const cache = window.__productRecommendationsCache;
    if (!cache.content) return;

    const cachedContent = cache.content;
    const cachedHeight = cache.contentHeight || 0;
    const willUpdate = this.innerHTML.trim() !== cachedContent.trim();

    if (willUpdate && cachedHeight > 0) {
      this.style.minHeight = `${cachedHeight}px`;
    }

    const updated = this.updateContentIfDifferent(cachedContent);

    this.classList.add("product-recommendations--loaded");

    if (willUpdate && cachedHeight > 0) {
      requestAnimationFrame(() => {
        this.style.minHeight = "";
      });
    }
  }

  /**
   * Store current content + metadata in cache
   */
  updateCache(content, productId, sectionId) {
    const cache = window.__productRecommendationsCache;
    cache.content = content;
    cache.productId = productId;
    cache.sectionId = sectionId;
    cache.contentHeight = Math.round(this.getBoundingClientRect().height);
    cache.isLoading = false;
  }

  connectedCallback() {
    const productId = this.dataset.productId;
    if (!productId) return;

    const sectionId = this.dataset.sectionId || "";
    const cache = window.__productRecommendationsCache;

    // FIRST: Check if server already rendered recommendations
    const hasServerContent = this.hasServerRenderedContent();

    if (hasServerContent) {
      // Server rendered content exists - use it and cache it
      this.classList.add("product-recommendations--loaded");

      const currentContent = this.innerHTML;

      if (!this.isCacheValid(productId, sectionId) || currentContent.trim() !== (cache.content || "").trim()) {
        this.updateCache(currentContent, productId, sectionId);
      } else {
        // Cache matches content, ensure height is up to date
        cache.contentHeight = Math.round(this.getBoundingClientRect().height);
      }
      return;
    }

    // SECOND: No server content - check cache and restore if available
    if (this.isCacheValid(productId, sectionId)) {
      this.restoreFromCache();
      return;
    }

    // THIRD: Check if we're already loading for this product (prevent duplicate requests)
    if (
      cache.isLoading &&
      cache.productId === productId &&
      cache.sectionId === sectionId &&
      !this.isCacheValid(productId, sectionId)
    ) {
      // Another instance is loading the same product, wait for it
      this.waitForCache(productId, sectionId);
      return;
    }

    // FOURTH: No cache, no server content, not loading - load recommendations
    if (!this._isLoading) {
      this.loadRecommendations(productId, sectionId);
    }
  }

  waitForCache(productId, sectionId, maxWait = 5000) {
    const startTime = Date.now();
    const checkInterval = 100;

    const checkCache = () => {
      if (this.isCacheValid(productId, sectionId)) {
        this.restoreFromCache();
        return;
      }

      if (Date.now() - startTime < maxWait && window.__productRecommendationsCache.isLoading) {
        // Still loading, check again
        setTimeout(checkCache, checkInterval);
      } else {
        // Timeout or loading finished but no cache - this shouldn't happen, but load anyway
        if (!this._isLoading && this.dataset.productId) {
          this.loadRecommendations(productId, sectionId);
        }
      }
    };

    checkCache();
  }

  loadRecommendations(productId, sectionId) {
    const cache = window.__productRecommendationsCache;

    // Prevent duplicate concurrent requests
    if (this._isLoading || (cache.isLoading && this.isCacheValid(productId, sectionId))) {
      return;
    }

    // Mark as loading
    cache.isLoading = true;
    this._isLoading = true;
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}`;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          const content = recommendations.innerHTML;

          // Update cache with new content
          this.updateCache(content, productId, sectionId);

          // Restore from cache (handles minHeight adjustment)
          this.restoreFromCache();
        } else {
          this.remove();
          cache.isLoading = false;
        }

        if (
          (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) ||
          !recommendations?.innerHTML.trim().length
        ) {
          this.remove();
          cache.isLoading = false;
        }
      })
      .catch((e) => {
        console.error(e);
        this.remove();
        cache.isLoading = false;
      })
      .finally(() => {
        this._isLoading = false;
        if (!cache.content) {
          cache.isLoading = false;
        }
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener("storefront:signincompleted", this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

function redirectTo(url) {
  window.location.href = url;
}

document.querySelector(".search-modal__close-button.modal__close-button").addEventListener("focus", function () {
  this.style.border = "0.2rem solid #0038FF";
});

document.querySelector(".search-modal__close-button.modal__close-button").addEventListener("blur", function () {
  this.style.removeProperty("border");
});

document.querySelector(".search-modal__clear-button").addEventListener("focus", function () {
  this.style.border = "0.2rem solid #0038FF";
});

document.querySelector(".search-modal__clear-button").addEventListener("blur", function () {
  this.style.removeProperty("border");
});

const scrollPositions = {};

window.addEventListener("scroll", () => {
  scrollPositions[window.location.pathname] = window.scrollY;
});

window.addEventListener("popstate", () => {
  const y = scrollPositions[window.location.pathname] || 0;
  setTimeout(() => window.scrollTo(0, y), 0);
});

// Handle page restores from cache (important for Safari)
window.addEventListener("pageshow", (event) => {
  const y = scrollPositions[window.location.pathname] || 0;
  if (event.persisted) {
    setTimeout(() => {
      window.scrollTo(0, y);
    }, 0);
  }
});

document.addEventListener("DOMContentLoaded", function () {
  const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  if (isTouch) {
    document.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", function (e) {
        link.focus();
      });
    });
  }
});

function observeLazyImages(container = document) {
  if ("IntersectionObserver" in window) {
    const lazyLoadObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute("data-src");
            }

            const picture = img.closest("picture");
            if (picture) {
              picture.querySelectorAll("source[data-srcset]").forEach((source) => {
                source.srcset = source.dataset.srcset;
                source.removeAttribute("data-srcset");
              });
            }

            observer.unobserve(img);

            if (window.innerWidth >= 750) {
              const secondImg = img.closest(".product-image-container")?.querySelector(".secondary_image");
              if (secondImg?.dataset?.src) {
                secondImg.src = secondImg.dataset.src;
                secondImg.removeAttribute("data-src");

                const secondPicture = secondImg.closest("picture");
                if (secondPicture) {
                  secondPicture.querySelectorAll("source[data-srcset]").forEach((source) => {
                    source.srcset = source.dataset.srcset;
                    source.removeAttribute("data-srcset");
                  });
                }

                observer.unobserve(secondImg);
              }
            }
          }
        });
      },
      {
        rootMargin: "50px 0px",
        threshold: 0.01,
      },
    );

    container.querySelectorAll("img[data-src]").forEach((img) => {
      lazyLoadObserver.observe(img);
    });
  }
}

// Initial load
document.addEventListener("DOMContentLoaded", () => {
  observeLazyImages();
  document.querySelector(".header__icon--menu")?.classList?.remove("unclickable");
});

window.updateSavePercentageBadge = (container, variant) => {
  const parsePrice = (str) => parseFloat(str?.replace(/[^\d.-]/g, "") || 0) || 0;
  const compareAtPrice = parsePrice(variant.compare_at_price);
  const salePrice = parsePrice(variant.price);
  const hasSale = compareAtPrice > salePrice && salePrice > 0;
 
  const badges = container.querySelectorAll(".badge--bottom-left.sale-badge");
  const existingBadge = badges[0];
  const saveText = existingBadge?.textContent?.match(/^([^\d%]+)/)?.[1]?.trim() || "Save";
 
  badges.forEach((badge) => {
    if (hasSale) {
      const percentage = Math.round(((compareAtPrice - salePrice) / compareAtPrice) * 100);
      if (percentage >= 5) {
        badge.innerHTML = `${saveText}&nbsp;${percentage}%`;
        badge.closest(".product__media_bottom-left-badge, .featured-card_bottom-left-badge")?.style.setProperty("display", "");
      } else {
        badge.closest(".product__media_bottom-left-badge, .featured-card_bottom-left-badge")?.style.setProperty("display", "none");
      }
    } else {
      badge.closest(".product__media_bottom-left-badge, .featured-card_bottom-left-badge")?.style.setProperty("display", "none");
    }
  });
};

async function removeOfflineCartItems() {
  try {
    const offlineItems = document.querySelectorAll('.cart-drawer [data-is-offline="true"][data-item-key], .cart-page [data-is-offline="true"][data-item-key]');
    if (offlineItems.length === 0) {
      return { removed: false, count: 0 };
    }
 
    const updates = {};
    offlineItems.forEach((item) => {
      const itemKey = item.getAttribute('data-item-key');
      if (itemKey) {
        updates[itemKey] = 0;
      }
    });
 
    if (Object.keys(updates).length > 0) {
      await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ updates }),
      });
    }
 
    return { removed: true, count: offlineItems.length };
  } catch (err) {
    console.warn("Failed to remove offline products:", err);
    return { removed: false, count: 0 };
  }
}
